import { PORTABLE_SCHEMA_VERSION } from './format.ts';
import { emptyHotlist } from '../rules/hotlist.ts';

/**
 * Migration chain for older exported files.
 *
 * Each migration takes the whole file object at version N and returns it at
 * version N+1. Add new steps to the end of this list; never edit an existing
 * one, or previously exported files will stop loading.
 */
export type Migration = (file: Record<string, unknown>) => Record<string, unknown>;

export const MIGRATIONS: Record<number, Migration> = {
  /**
   * 1 -> 2: `customContent` becomes a typed record of player-authored
   * definitions instead of an unused `unknown[]`.
   *
   * Schema 1 never wrote anything into that list - custom content did not exist
   * when it was defined - so an empty record loses nothing. A version 1 file
   * whose crawler DOES reference a custom ID could not have carried the
   * definition either way; the import reports it as unresolved rather than
   * pretending it was there.
   */
  1: (file) => ({ ...file, customContent: { skills: [], backgrounds: [], items: [] } }),
  /**
   * 2 -> 3: custom Items join the package.
   *
   * A schema 2 file carried none, so an empty list loses nothing. A crawler in
   * one that references a custom Item is reported as an unresolved dependency
   * rather than pretended to be satisfiable.
   */
  2: (file) => {
    const content = file['customContent'];
    const existing = typeof content === 'object' && content !== null ? content : {};
    return { ...file, customContent: { items: [], ...(existing as object) } };
  },
  /**
   * 3 -> 4: embedded custom Items state whether they are physical weapons.
   *
   * Bumping the custom Item LIBRARY schema was not enough on its own. A schema
   * 3 export already embeds custom Items in the old shape, and `weaponAttackId`
   * is required-and-nullable, so without this step a crawler exported last week
   * would fail validation on import for lacking a field that did not exist when
   * it was written. Migrating the file, not only the library, is what keeps
   * that promise.
   *
   * Null for every one of them, and never guessed from the name (OPEN-011).
   */
  3: (file) => {
    const content = file['customContent'];
    if (typeof content !== 'object' || content === null) return file;
    const record = content as Record<string, unknown>;
    const items = Array.isArray(record['items']) ? record['items'] : [];
    return {
      ...file,
      customContent: {
        ...record,
        items: items.map((entry) => {
          if (typeof entry !== 'object' || entry === null) return entry;
          // Canonicalised AFTER the spread. A schema 3 export could not carry a
          // meaningful `weaponAttackId`, so a file containing one is either
          // hand-edited or corrupt; either way it must not gain mechanical
          // semantics on import because a later version named the field.
          return { ...(entry as Record<string, unknown>), weaponAttackId: null };
        }),
      },
    };
  },
  /**
   * 4 -> 5: the embedded crawler carries a ten-slot Hotlist.
   *
   * WHY THE FILE VERSION MOVES AT ALL, when the Character schema already
   * migrates itself: so an OLDER build refuses this file cleanly. A schema 4
   * file whose character declares character-schema 8 would reach an older app
   * that stops migrating at 7 and hands the record on unchanged, carrying a
   * field it does not understand. Bumping the file version makes that a clear
   * "exported by a newer version of the app" message instead.
   *
   * Canonicalised AFTER the spread, the same lesson as 3 -> 4: a schema 4
   * export could not carry a Hotlist, so one that has entries is hand-edited or
   * corrupt and must not acquire ten slots' worth of configuration on import.
   *
   * TEN EMPTY SLOTS, not a missing field. Leaning on the crawler's own 7 -> 8
   * migration to fill it in would only work when the embedded character is
   * BELOW schema 8; one that already declares 8 - hand-edited, or exported by a
   * build mid-upgrade - skips every migration step and would arrive with no
   * Hotlist at all, failing validation for a field the file was never able to
   * carry. The file migration has to produce the canonical value itself.
   */
  4: (file) => {
    const character = file['character'];
    if (typeof character !== 'object' || character === null) return file;
    return {
      ...file,
      character: { ...(character as Record<string, unknown>), hotlist: emptyHotlist() },
    };
  },
  /**
   * 5 -> 6: Hotlist Item slots carry a count.
   *
   * Schema 5 existed for one review cycle. Its Item entries had no quantity, so
   * each becomes ONE copy - the conservative reading that preserves the
   * shortcut without claiming the crawler's whole stack was allocated to it.
   *
   * The file version moves for the same reason 4 -> 5 did: so an older build
   * refuses the file cleanly rather than passing on a character record carrying
   * a field it does not understand. The crawler's own 8 -> 9 step would also
   * fill this in, but only for a character BELOW schema 9; one already claiming
   * 9 skips every migration, so the file step does the work itself.
   */
  5: (file) => {
    const character = file['character'];
    if (typeof character !== 'object' || character === null) return file;
    const record = character as Record<string, unknown>;
    const hotlist = record['hotlist'];
    if (!Array.isArray(hotlist)) return file;
    return {
      ...file,
      character: {
        ...record,
        hotlist: hotlist.map((slot) => {
          if (typeof slot !== 'object' || slot === null) return slot;
          const entry = slot as Record<string, unknown>;
          if (entry['kind'] !== 'item') return entry;
          return { ...entry, quantity: 1 };
        }),
      },
    };
  },
  /**
   * 6 -> 7: embedded custom Items may carry a typed `use` effect.
   *
   * THE FILE VERSION IS THE CONTRACT for the custom Item shape. An imported
   * custom Item is checked against whatever validator this build has - there is
   * no per-Item version inside the file - so the only thing saying what an
   * embedded definition may mean is the file's own version. `use` being
   * optional in TypeScript is compiler convenience and says nothing about
   * persisted semantics.
   *
   * A schema 6 export could not carry a use effect, so any `use` on an embedded
   * Item is hand-edited and is DELETED rather than trusted. Nothing is inferred
   * from the name, the kind or the description: a legacy Item called "Mana
   * Potion", or one whose description says it restores Mana, arrives inert,
   * exactly as migration 2 -> 3 refused to guess `weaponAttackId` from a name.
   *
   * Absence is the canonical inert form, so the field is removed rather than
   * written as null - `null` belongs to the editor draft and nowhere else.
   */
  6: (file) => {
    const content = file['customContent'];
    if (typeof content !== 'object' || content === null) return file;
    const record = content as Record<string, unknown>;
    const items = record['items'];
    if (!Array.isArray(items)) return file;
    return {
      ...file,
      customContent: {
        ...record,
        items: items.map((entry) => {
          if (typeof entry !== 'object' || entry === null) return entry;
          const { use: _discarded, ...rest } = entry as Record<string, unknown>;
          return rest;
        }),
      },
    };
  },
  /**
   * 7 -> 8: the embedded crawler's Inventory entries gain `held`.
   *
   * RULE DCC-INV-018. Schema 7 had no way to record a copy physically in a hand
   * without being in its Gear Slot, so every entry arrives at zero.
   *
   * **THIS STEP DOES THE WORK ITSELF** rather than leaning on the crawler's own
   * 9 -> 10 step, for exactly the reason 5 -> 6 gives: a character record ALREADY
   * claiming schema 10 skips every character migration, so a hand-edited file
   * could carry a `held` value schema 7 could not legitimately have authored.
   * Canonicalised AFTER the spread so such a value is overwritten, not kept.
   *
   * Nothing is reinterpreted. `equipped` is not consulted: every weapon, shield
   * and piece of armour arrives exactly as it left.
   */
  7: (file) => {
    const character = file['character'];
    if (typeof character !== 'object' || character === null) return file;
    const record = character as Record<string, unknown>;
    const inventory = record['inventory'];
    if (!Array.isArray(inventory)) return file;
    return {
      ...file,
      character: {
        ...record,
        inventory: inventory.map((entry) => {
          if (typeof entry !== 'object' || entry === null) return entry;
          return { ...(entry as Record<string, unknown>), held: 0 };
        }),
      },
    };
  },
  /**
   * 8 -> 9: an embedded custom Passive Skill gains the `passive` TRAIT.
   *
   * RULE DCC-SKILL-002. Schema 8 carried "makes no Check" in `kind` alone.
   * `kind` does NOT move - a custom Passive Skill is still categorically
   * Passive; only the behaviour became a trait.
   *
   * THIS STEP DOES THE WORK ITSELF rather than leaning on the custom Skill
   * library's own 1 -> 2 step, for the same reason 5 -> 6 and 7 -> 8 give: an
   * embedded definition has no independent schema version, so the file's
   * version is the only thing that contracts its meaning. Canonicalised AFTER
   * the spread so a hand-edited schema-8 file cannot smuggle contradictory
   * traits forward.
   */
  8: (file) => {
    const custom = file['customContent'];
    if (typeof custom !== 'object' || custom === null) return file;
    const content = custom as Record<string, unknown>;
    const skills = content['skills'];
    if (!Array.isArray(skills)) return file;
    return {
      ...file,
      customContent: {
        ...content,
        skills: skills.map((skill) => {
          if (typeof skill !== 'object' || skill === null) return skill;
          const entry = skill as Record<string, unknown>;
          return { ...entry, traits: entry['kind'] === 'passive' ? ['passive'] : [] };
        }),
      },
    };
  },
  /**
   * 9 -> 10: AttackProfiles become portable companions to custom Skills.
   *
   * Schema 9 had no authored AttackProfile field. Canonicalise AFTER spreading
   * the old customContent record so a stray hand-written `attacks` property does
   * not acquire executable schema-10 meaning merely because it has the right
   * spelling.
   */
  9: (file) => {
    const custom = file['customContent'];
    const content = typeof custom === 'object' && custom !== null ? custom as Record<string, unknown> : {};
    return {
      ...file,
      customContent: {
        ...content,
        attacks: [],
      },
    };
  },
  /**
   * 10 -> 11: embedded custom Items gain V5 Special Rules and modifier forms.
   *
   * Portable 10 never owned those semantics. Canonicalise AFTER spreading each
   * old Item so a hand-written `notes` field or future-looking modifier cannot
   * become executable merely because this build knows the spelling.
   */
  10: (file) => {
    const custom = file['customContent'];
    if (typeof custom !== 'object' || custom === null) return file;
    const content = custom as Record<string, unknown>;
    const items = Array.isArray(content['items']) ? content['items'] : [];
    const newKinds = new Set([
      'skill-rank',
      'elemental-resistance',
      'vulnerability',
      'immunity',
    ]);
    return {
      ...file,
      customContent: {
        ...content,
        items: items.map((entry) => {
          if (typeof entry !== 'object' || entry === null) return entry;
          const { notes: _discardedNotes, ...item } = entry as Record<string, unknown>;
          const modifiers = Array.isArray(item['modifiers']) ? item['modifiers'] : [];
          return {
            ...item,
            modifiers: modifiers.filter((modifier) => {
              if (typeof modifier !== 'object' || modifier === null) return true;
              const target = (modifier as Record<string, unknown>)['target'];
              if (typeof target !== 'object' || target === null) return true;
              return !newKinds.has(String((target as Record<string, unknown>)['kind']));
            }),
          };
        }),
      },
    };
  },
};

export function migrateToCurrent(file: Record<string, unknown>): Record<string, unknown> {
  let current = file;
  let version = typeof current['schemaVersion'] === 'number' ? current['schemaVersion'] : 0;

  if (version < 1) {
    throw new Error('This file does not declare a schema version and cannot be read.');
  }
  if (version > PORTABLE_SCHEMA_VERSION) {
    throw new Error(
      `This file was exported by a newer version of the app (schema ${version}). Update the app and try again.`,
    );
  }

  while (version < PORTABLE_SCHEMA_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) throw new Error(`No migration available from schema version ${version}.`);
    current = step(current);
    version += 1;
    current['schemaVersion'] = version;
  }
  return current;
}
